import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupWorkshop } from '../../../../../_shared/models/lookup.model';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { AssignRoutineComplianceTaskWorkshopRequest, RoutineComplianceTask } from '../../../models/compliance-item.model';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-assign-routine-compliance-task-workshop-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './assign-routine-compliance-task-workshop-modal.component.html',
  styleUrl: './assign-routine-compliance-task-workshop-modal.component.scss'
})
export class AssignRoutineComplianceTaskWorkshopModalComponent implements OnDestroy {
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private complianceOverviewService = inject(ComplianceOverviewService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();

  private initialWorkshopId: number | null = null;
  private routineComplianceTask!: RoutineComplianceTask;

  @Input()
  set task(value: RoutineComplianceTask) {
    this.routineComplianceTask = value;
    if (value) {
      void this.loadWorkshopsAsync();
    }
  }

  get task(): RoutineComplianceTask {
    return this.routineComplianceTask;
  }

  @Input() accountIds: number[] = [];
  @Input() accountId?: number;

  assignWorkshopForm = this.fb.group({
    workshopId: [null as number | null, Validators.required]
  });

  workshops: LookupWorkshop[] = [];
  selectedWorkshopName = '';
  isLoading = false;
  isLoadingWorkshops = false;

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
  }

  get workshopLabel(): string {
    return this.selectedWorkshopName || this.transloco.translate('compliance.overview.assignWorkshopModal.selectWorkshop');
  }

  get hasChanges(): boolean {
    return this.assignWorkshopForm.getRawValue().workshopId !== this.initialWorkshopId;
  }

  closeModal(): void {
    this.activeModal.dismiss();
  }

  onCancel(): void {
    this.closeModal();
  }

  onWorkshopSelect(workshop: LookupWorkshop, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.selectedWorkshopName = workshop.name;
    this.assignWorkshopForm.get('workshopId')?.setValue(workshop.id);
    this.assignWorkshopForm.get('workshopId')?.markAsTouched();
  }

  async onSubmit(): Promise<void> {
    if (this.assignWorkshopForm.invalid || !this.hasChanges || this.isLoading) {
      this.assignWorkshopForm.markAllAsTouched();
      return;
    }

    const workshopId = this.assignWorkshopForm.getRawValue().workshopId;
    if (workshopId === null) {
      return;
    }

    this.isLoading = true;

    const request: AssignRoutineComplianceTaskWorkshopRequest = {
      id: this.task.id,
      workshopId,
      assetId: this.task.assetId
    };

    try {
      await this.complianceOverviewService.assignRoutineComplianceTaskWorkshopAsync(request, this.cancellation);
      this.toastr.success(this.transloco.translate('compliance.overview.assignWorkshopModal.toasts.assigned'));
      this.isLoading = false;
      this.activeModal.close(this.selectedWorkshopName);
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        showToast: true,
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  private async loadWorkshopsAsync(): Promise<void> {
    this.isLoadingWorkshops = true;

    try {
      const response = this.accountId
        ? await this.lookupService.getWorkshopsByAccountHierarchyAsync(this.accountId, this.cancellation)
        : await this.lookupService.getWorkshopsAsync(this.accountIds);
      this.workshops = response.data ?? [];
      this.resolveInitialSelection();
      this.isLoadingWorkshops = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoadingWorkshops = false;
        }
      });
    }
  }

  private resolveInitialSelection(): void {
    const currentWorkshop = this.workshops.find(w => this.normalize(w.name) === this.normalize(this.task.workshop));

    if (currentWorkshop) {
      this.selectedWorkshopName = currentWorkshop.name;
      this.assignWorkshopForm.get('workshopId')?.setValue(currentWorkshop.id);
      this.initialWorkshopId = currentWorkshop.id;
      return;
    }

    this.selectedWorkshopName = this.task.workshop ?? '';
    this.initialWorkshopId = null;
  }

  private normalize(value?: string | null): string {
    return (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  }
}